import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TableRequestService } from '../../../../base/services/table-request/table-request.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatPaginator } from '@angular/material/paginator';
import { TableService } from './table-service';
import { TableCsvService } from '../../../../base/services/table-csv/table-csv.service';
import { CSVGroupLabel } from '../../../../models/csv-types';
import { TableSettings, typeColumns, ConfigSettings } from '../../../../models/column-types';
import { TableFiltersField, TableFiltersOptions, TableFiltersOutput } from '../../../../models/table-filters';
import { LoadingService } from '../../../../shared/loading/loading.service';
import { ColumnsTable } from '../../../../models/interfaces';
import { TranslateService } from '../../../../core/translation';
import { Area } from '../../../../api';
import { CustomFields } from '../../../../api/model/customFields';
import { OrderTypeColumn, PageFormatTable } from './types/table-types';
import { AuxiliarOption, AuxiliarOptions } from '../../../types/signaturit/shared-types';
import { TranslateSearchService } from '../../../../base/services/translate-search/translate-search.service';

export interface ButtonMenu {
  key: string;
  value: string;
  disabled?: boolean;
}

@Component({
  selector: 'signaturit-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() tableId: string;
  @Input() tableTitle: string;
  @Input() data: unknown[];
  @Input() customFields: CustomFields[];
  @Input() order: string = null;
  @Input() columns: ColumnsTable[];
  @Input() kebabMenuOptions: ButtonMenu[];
  @Input() kebabMenuOptionsFilter: (el: unknown) => ButtonMenu[];
  @Input() actionsMenuOptions: ButtonMenu[];
  @Input() urlData: string;
  @Input() countRegister: number = 0;
  @Input() hasCSVBtn: boolean = false;
  @Input() hasActionsBtn: boolean = false;
  @Input() labelsCSV: CSVGroupLabel;
  @Input() gridSettings!: TableSettings;
  @Input() config: ConfigSettings;
  @Input() filtersFields: TableFiltersField[];
  @Input() filtersTitle: string = this._translate.instant('label_filters');
  @Input() filtersOptions: TableFiltersOptions;
  @Input() noHeader: boolean;
  @Input() pageSize: number = 10;
  @Input() mode: number = 1;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Output() onRowDblClick = new EventEmitter();
  @Output('clickKebabOption') clickKebabOption: EventEmitter<AuxiliarOption<unknown>> = new EventEmitter<
    AuxiliarOption<unknown>
  >();
  @Output('clickActionOption') clickActionOption: EventEmitter<AuxiliarOptions<unknown>> = new EventEmitter<
    AuxiliarOptions<unknown>
  >();
  @Output() totalRegisters: EventEmitter<number> = new EventEmitter<number>();
  @Output() onFiltersSet: EventEmitter<unknown> = new EventEmitter<unknown>();
  @Output() setDataSourceEvent: EventEmitter<Area[]> = new EventEmitter<Area[]>();
  @Output() toggleSubareasEvent: EventEmitter<Area> = new EventEmitter<Area>();

  keyColumns: string[] = [];
  displayedColumns: ColumnsTable[];
  columnsToShow: string[] = [];
  dataSource = [];
  dynamicWidth: string = '';
  allSelected: boolean = false;
  elementsSelected: unknown[] = [];
  destroy$: Subject<boolean> = new Subject<boolean>();
  changePage: boolean = false;
  currentKeyTable: string = '';
  currentOrder: string = OrderTypeColumn.DESC;
  arrowIcon: string = '';
  keyMultiple: string = '';

  constructor(
    private _dataService: TableRequestService,
    private _table: TableService,
    private _loading: LoadingService,
    private _csvService: TableCsvService,
    private _translateSearch: TranslateSearchService,
    private _translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.mode === 2) {
      this.dataSource = this.customFields;
    }
    this.displayedColumns = this.columns;
    this.dynamicWidth = 100 / this.displayedColumns.length + '%';
    const cacheFilters = this._table.getCacheFilters(this.tableId);
    if ((!this.customFields || this.mode === 1) && !cacheFilters) {
      this.getData();
    }
    this.loadCSVConfig();
    this.listenReloadTable();
    this.listenClearSelecion();
    this.listenSearch();
    this.addOptionsColumn();
    this.validatorNumber();
  }

  ngAfterViewInit(): void {
    this.listenChangePage();
  }

  ngOnChanges(): void {
    if (this.customFields && this.mode === 2) {
      this.dataSource = this.customFields;
    } else if (this.data && this.mode === 1) {
      this.dataSource = this.data;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  validatorNumber(): void {
    if (this.dataSource) {
      for (const data of this.dataSource) {
        if (data['PhoneNumber']) {
          data['PhoneNumber'] = data['PhoneNumber'].replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4');
        }
      }
    }
  }

  addOptionsColumn(): void {
    for (const d of this.displayedColumns) {
      if (d.visible) {
        this.columnsToShow.push(d.key);
      }

      if (d.isKeyMultiple) {
        this.keyMultiple = d.value;
      }
    }
  }

  getData(filter: string = null, concatFilter: boolean = false): void {
    this._loading.set(true);
    this._csvService.filter = filter;
    this._dataService.getData(filter, this.urlData, this.order, this.changePage, false, concatFilter).subscribe(
      (response) => {
        const items = response.Items || response.items;
        this.dataSource = items;
        if (this.allSelected) {
          for (let rowData of this.dataSource) {
            if (!this.isSelected(rowData, this.currentKeyTable)) {
              this.elementsSelected.push(rowData);
            }
          }
        }
        const count = response.Count || response.count;
        this.countRegister = count;
        this.totalRegisters.emit(this.countRegister);

        if (!this.changePage) {
          this.paginator.firstPage();
        }

        this.paginator.pageIndex = parseInt(this._dataService.getPage());
        this.validatorNumber();
        this.changePage = false;
        this._loading.set(false);
        this.setDataSourceEvent.emit(this.dataSource);
      },
      (error) => {
        console.log(error);
        this._loading.set(false);
      }
    );
  }

  sortColumns(columnKey: string): void {
    if (this.currentOrder === OrderTypeColumn.ASC) {
      this.currentOrder = OrderTypeColumn.DESC;
      this.arrowIcon = 'arrow_drop_up';
    } else {
      this.currentOrder = OrderTypeColumn.ASC;
      this.arrowIcon = 'arrow_drop_down';
    }

    this.order = `${columnKey} ${this.currentOrder}`;
    this.getData(this._csvService.filter);
  }

  selectAll(event: boolean): void {
    this.allSelected = event;
    this.elementsSelected = [];
    if (event) {
      for (const data of this.dataSource) {
        this.elementsSelected.push(data);
      }
    } else {
      this.elementsSelected = [];
    }
  }

  onSelectRow(event: boolean, rowData: unknown, key: string): void {
    if (this.tableId === 'reassign_table') {
      key = 'idOperation';
    }

    this.currentKeyTable = key;
    if (event) {
      if (this.keyMultiple) this.validateGroup(rowData);
      else this.elementsSelected.push(rowData);
    } else {
      this.elementsSelected = this.elementsSelected.filter((selectedElement) => {
        return selectedElement[key] !== rowData[key];
      });
    }
  }

  isSelected(rowData: unknown, key: string): boolean {
    if (this.tableId === 'reassign_table') {
      key = 'idOperation';
    }

    this.currentKeyTable = key;
    for (const selectedElement of this.elementsSelected) {
      if (selectedElement[key] !== undefined && rowData[key] !== undefined && selectedElement[key] === rowData[key]) {
        return true;
      }
    }
  }

  onclickKebab(event: AuxiliarOption<unknown>): void {
    this.clickKebabOption.emit({ action: event.action, row: event.row });
  }

  onclickAction(actionKey: string): void {
    this.clickActionOption.emit({ action: actionKey, rows: this.elementsSelected });
  }

  onRowDoubleClick(row: typeColumns): void {
    this.onRowDblClick.emit(row);
  }

  listenReloadTable(): void {
    this._dataService.reloadAction.pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        if (this.paginator) {
          this._dataService.skip = parseInt(this.paginator?.pageIndex.toString() + '0');
          this.changePage = true;
        }

        this.getData();
      },
      (error) => {
        console.log(error);
      }
    );
  }

  listenSearch(): void {
    this._translateSearch.searchConverted.pipe(takeUntil(this.destroy$)).subscribe(
      (response: string) => {
        this._dataService.skip = 0;
        this.getData(response, true);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  listenClearSelecion(): void {
    this._table.clear.subscribe((response: boolean) => {
      if (response) {
        this.selectAll(false);
      }
    });
  }

  listenChangePage(): void {
    this.paginator?.page.subscribe(
      (page: PageFormatTable) => {
        this._dataService.skip = parseInt(page.pageIndex.toString() + '0');
        this.changePage = true;
        this.getData();
      },
      (error: any) => {
        console.log(error);
      }
    );
  }

  downloadCSV(): void {
    this._csvService.exportDataCSVyes(0);
  }

  openCSV(): void {
    this._csvService.exportDataCSV();
  }

  loadCSVConfig(): void {
    this._csvService.gridSettings = this.gridSettings;
    this._csvService.dataExportCsv = [];
    this._csvService.columns = this.columns;
    this._csvService.keyColumns = { ...this.keyColumns };
    this._csvService.keyColumns = this.loadNewKeys();
    this._csvService.labelsCsv = this.labelsCSV;
    this._csvService.order = this.gridSettings?.orderBy;
    this._csvService.serviceConfig = this._dataService.getService();
    this._csvService.endPoint = this.config?.endPoint;
  }

  loadNewKeys(): string[] {
    const newKeyColumns = [];
    for (const key in this._csvService.keyColumns) {
      newKeyColumns.push(this._csvService.keyColumns[key]);
    }
    return newKeyColumns;
  }

  filterData(event: unknown[]): void {
    this.dataSource = event;
  }

  updateRegister(event: number): void {
    this.countRegister = event;
  }

  setFilters(filtersOutput: TableFiltersOutput): void {
    this.onFiltersSet.emit(filtersOutput.object);
    this.getData(filtersOutput.ODataFilters);
  }

  toggleSubareas(area: Area): void {
    this.toggleSubareasEvent.emit(area);
  }

  getStringObject(object: unknown): string {
    return JSON.stringify(object);
  }

  getClassState(idState: number, status: string): string {
    const active: number[] = [1, 2];
    const expired: number[] = [3, 4, 5, 6, 8];
    const pending: number[] = [0, 7];

    if (status === 'ACTIVE' || active.includes(idState)) {
      return 'activeState';
    } else if (status === 'INACTIVE' || expired.includes(idState)) {
      return 'expiredState';
    } else if (pending.includes(idState)) {
      return 'pendingState';
    } else {
      return '';
    }
  }

  validateGroup(row: unknown): void {
    this.dataSource.forEach((data) => {
      if (data['idCircuit'] === row['idCircuit']) {
        this.elementsSelected.push(data);
      }
    });
  }
}
